import { TimePanel } from '../components/TimePanel';
import { ContractPanel } from '../components/ContractPanel';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRY_INFO,
  BILLING_LABEL, CHANNEL_LABEL, HOLD_REASONS, billingFor, needsEstimate, HOLD_REASON_LABEL, IMPACT_LABEL, LEVELS, PRIORITIES, PRIORITY_LABEL,
  RESOLUTION_CODES, RESOLUTION_LABEL, STATUS_LABEL, TICKET_TYPE_LABEL, URGENCY_LABEL,
  type Level, type Priority, type TicketStatus, type TicketType, type TransitionOption
} from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { PriorityBadge, StatusText, TypeBadge } from '../components/Badges';
import { SlaMeter } from '../components/SlaMeter';
import { StageLine } from '../components/StageLine';
import { EstimateQuote } from '../components/EstimateQuote';
import { ChangeCard } from '../components/ChangeCard';
import { Conversation } from '../components/Conversation';
import { AgentOptions } from '../components/AgentOptions';
import { Modal, area, btnPrimary, btnSecondary, field } from '../components/Modal';
import { api, ApiError, type AuditEntry, type InitiativeOption, type Meta, type Ticket, type TicketDetail as Detail } from '../lib/api';
import { useSession } from '../lib/session';
import { dateTime, relativeTime } from '../lib/format';

function actionLabel(t: Ticket, o: TransitionOption): string {
  if (o.reopen) return 'Reabrir';
  if (t.status === 'on_hold' && o.to === t.resumeTo) return 'Reanudar';
  if (t.status === 'pending_approval' && o.to === 'estimating') return 'El cliente pidió ajustes';
  const labels: Partial<Record<TicketStatus, string>> = {
    assigned: 'Marcar como asignado',
    estimating: 'Iniciar estimación',
    pending_approval: 'Enviar estimado al cliente',
    approved: t.status === 'pending_approval' ? 'Registrar aprobación del cliente' : 'Aprobar (sin aprobación del cliente)',
    in_progress: 'Iniciar trabajo',
    on_hold: 'Poner en espera',
    resolved: 'Resolver',
    closed: 'Cerrar',
    cancelled: 'Cancelar ticket',
    rejected: 'Registrar rechazo del cliente',
    converted: 'Convertir a proyecto'
  };
  return labels[o.to] ?? STATUS_LABEL[o.to];
}

const FIELD_LABEL: Record<string, string> = {
  status: 'Estado', assigneeId: 'Agente', assigneeName: 'Agente', impact: 'Impacto', urgency: 'Urgencia',
  priority: 'Prioridad', priorityOverride: 'Prioridad manual', category: 'Categoría', initiativeId: 'Iniciativa',
  initiativeCode: 'Iniciativa', subject: 'Asunto', description: 'Descripción', type: 'Tipo', estimate: 'Estimado',
  resolution: 'Resolución', holdReason: 'Motivo de espera', holdNote: 'Tercero / referencia', slaClock: 'Reloj', quote: 'Cotización', attachment: 'Archivo', groupName: 'Grupo'
};
const HIDDEN_FIELDS = new Set(['hoursRelease', 'overrunAlertedAt', 'open', 'resumeTo', 'timestamps', 'initiativeName', 'assigneeId', 'initiativeId', 'reopenCount', 'closeReason', 'groupId']);

function describeValue(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'status') return STATUS_LABEL[v as TicketStatus] ?? String(v);
  if (field === 'impact') return IMPACT_LABEL[v as Level]?.split('·')[0]?.trim() ?? String(v);
  if (field === 'urgency') return URGENCY_LABEL[v as Level] ?? String(v);
  if (field === 'type') return TICKET_TYPE_LABEL[v as TicketType] ?? String(v);
  if (field === 'holdReason') return HOLD_REASON_LABEL[v as keyof typeof HOLD_REASON_LABEL] ?? String(v);
  if (field === 'category') return (v as { label?: string }).label ?? '—';
  if (field === 'priorityOverride') return (v as { value?: string }).value ?? '—';
  if (field === 'estimate') return `${(v as { hours: number }).hours} h`;
  if (field === 'resolution') return RESOLUTION_LABEL[(v as { code: keyof typeof RESOLUTION_LABEL }).code] ?? '—';
  return String(v);
}

function AuditItem({ entry }: { entry: AuditEntry }) {
  const changes = entry.changes.filter(c => !HIDDEN_FIELDS.has(c.field));
  const title = entry.action === 'created' ? 'creó el ticket'
    : entry.action === 'reclassified' ? 'reclasificó el ticket'
    : entry.action === 'hours_release' ? 'permitió capturar horas en el ticket cerrado'
    : entry.action === 'overrun' ? 'detectó sobreconsumo de horas'
    : entry.action === 'attachment:download' ? 'descargó un archivo sensible'
    : entry.action === 'attachment:sensitive' ? 'marcó un archivo como sensible'
    : entry.action === 'attachment:not_sensitive' ? 'quitó la marca de sensible a un archivo'
    : entry.action === 'quote:sent' ? 'envió la cotización al cliente'
    : entry.action === 'estimate' ? 'registró el estimado'
    : entry.action === 'sla:75' ? 'avisó: SLA al 75 %'
    : entry.action === 'sla:100' ? 'avisó: SLA vencido'
      : entry.action.startsWith('status:') ? `cambió el estado a ${STATUS_LABEL[entry.action.slice(7) as TicketStatus] ?? ''}`
        : 'actualizó el ticket';
  return (
    <li className="border-l-2 border-line pb-4 pl-4 last:pb-0">
      <p className="text-sm"><strong className="font-semibold">{entry.actor?.name}</strong> {title}
        <span className="text-muted"> · {relativeTime(entry.at)}</span></p>
      {entry.action !== 'created' && changes.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-muted">
          {changes.map(c => (
            <li key={c.field}>{c.field === 'attachment'
              ? <>Archivo: <span className="text-ink">{String(c.after)}</span></>
              : <>{FIELD_LABEL[c.field] ?? c.field}: {describeValue(c.field, c.before)} → <span className="text-ink">{describeValue(c.field, c.after)}</span></>}</li>
          ))}
        </ul>
      )}
      {entry.reason && <p className="mt-1 text-xs italic text-[#3F3D39]">“{entry.reason}”</p>}
    </li>
  );
}

// collapsible: panel lateral plegable (H3 · homologación); el título queda siempre visible.
function Card({ title, children, aside, collapsible, defaultOpen = true }: {
  title: string; children: ReactNode; aside?: ReactNode; collapsible?: boolean; defaultOpen?: boolean;
}) {
  if (collapsible) {
    return (
      <details open={defaultOpen} className="group rounded-lg border border-line bg-white">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-2 px-4 [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"
              className="-rotate-90 text-muted transition-transform group-open:rotate-0"><path d="M6 9l6 6 6-6" /></svg>
            <h2 className="text-sm font-semibold">{title}</h2>
          </span>
          {aside}
        </summary>
        <div className="px-4 pb-4">{children}</div>
      </details>
    );
  }
  return (
    <section className="rounded-lg border border-line bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export default function TicketDetail() {
  const { number = '' } = useParams();
  const { t } = useTranslation();
  const { me } = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<TransitionOption | null>(null);
  const [reclassify, setReclassify] = useState<TicketType | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [flash, setFlash] = useState<string>((location.state as { created?: boolean } | null)?.created ? 'Ticket creado.' : '');
  const [error, setError] = useState('');

  const query = useQuery({ queryKey: ['ticket', number], queryFn: () => api<Detail>(`/tickets/${number}`), refetchInterval: 60_000 });
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => api<Meta>('/meta'), staleTime: 5 * 60_000 });
  const ticket = query.data?.ticket;
  const initiatives = useQuery({
    queryKey: ['initiatives', ticket?.organizationId],
    queryFn: () => api<InitiativeOption[]>(`/organizations/${ticket!.organizationId}/initiatives`),
    enabled: Boolean(ticket?.organizationId),
    staleTime: 5 * 60_000
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', number] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };
  const onError = (err: unknown) => setError(err instanceof ApiError ? err.message : t('errors.generic'));

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/tickets/${number}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => { setError(''); refresh(); },
    onError
  });

  if (query.isLoading) return <Shell><p role="status" className="py-10 text-center text-sm text-muted">{t('common.loading')}</p></Shell>;
  if (query.isError || !ticket || !query.data) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Shell>
        <div className="rounded-lg border border-line bg-white p-8 text-center">
          <h1 className="text-lg font-semibold">{notFound ? 'No encontramos ese ticket' : t('errors.generic')}</h1>
          <Link to="/" className="mt-3 inline-block text-sm text-accent underline">Volver a la bandeja</Link>
        </div>
      </Shell>
    );
  }

  const transitions = query.data.transitions;
  const editable = ticket.open;
  const label = 'text-xs text-muted';
  const small = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2 text-sm';

  return (
    <Shell>
      <div className="flex flex-col gap-5">
        <Link to="/" className="text-sm text-accent hover:underline">← Bandeja</Link>

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-[1_1_480px]">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono font-medium">{ticket.number}</span>
              <TypeBadge type={ticket.type} />
              <PriorityBadge priority={ticket.priority} />
              <StatusText status={ticket.status} holdReason={ticket.holdReason} holdNote={ticket.holdNote} />
            </div>
            <h1 className="mt-2 text-2xl font-semibold leading-tight">{ticket.subject}</h1>
            {(query.data.sla.health !== 'none' || query.data.response?.health !== 'none') && (
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted"
                title={`Horario hábil de ${COUNTRY_INFO[query.data.slaCalendar?.country ?? 'MX'].name}: no cuentan fines de semana ni festivos${ticket.priority === 'P1' ? ' (P1 corre 24×7)' : ''}`}>
                {query.data.response && query.data.response.health !== 'none' && (
                  <span className="inline-flex items-center gap-2"><span>SLA primera respuesta</span><SlaMeter sla={query.data.response} size="md" /></span>
                )}
                {query.data.sla.health !== 'none' && (
                  <span className="inline-flex items-center gap-2">
                    <span>{query.data.sla.clock === 'estimate' ? 'SLA entrega de estimado' : 'SLA resolución'}</span>
                    <SlaMeter sla={query.data.sla} size="md" />
                  </span>
                )}
                <span className="rounded bg-[#EDEBE7] px-1.5 py-0.5">{query.data.slaCalendar?.country ?? 'MX'}</span>
                {query.data.slaCalendar?.commitment === 'reference' && (
                  <span className="rounded bg-[#F3EADF] px-1.5 py-0.5 text-[#8A5A2B]" title="Bolsa de horas: el SLA se mide como referencia, sin compromiso contractual ni escalamiento">
                    Referencia · mejor esfuerzo
                  </span>
                )}
              </div>
            )}
            <p className="mt-1 text-sm text-muted">
              {ticket.organizationName} · creado por {ticket.createdBy.name} {relativeTime(ticket.createdAt)} · {CHANNEL_LABEL[ticket.channel]}
            </p>
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              {transitions.map((o, i) => (
                <button key={o.to + String(o.reopen)} type="button" className={i === 0 ? btnPrimary : btnSecondary}
                  onClick={() => { setError(''); setPending(o); }}>
                  {actionLabel(ticket, o)}
                </button>
              ))}
              {ticket.status !== 'converted' && (
                <button type="button" className={btnSecondary}
                  onClick={() => setReclassify(ticket.type === 'INC' ? 'REQ' : 'INC')}>Reclasificar</button>
              )}
            </div>
          )}
          {!editable && transitions.length > 0 && (
            <div className="flex gap-2">
              {transitions.map(o => (
                <button key={o.to} type="button" className={btnSecondary} onClick={() => setPending(o)}>{actionLabel(ticket, o)}</button>
              ))}
            </div>
          )}
        </header>

        {ticket.needsTriage && editable && (
          <p className="rounded-md bg-[#EEF3F8] px-4 py-2 text-sm text-[#28445E]">
            Este ticket llegó por correo y se creó como <strong className="font-semibold">Incidente P3</strong>. Revisa el tipo, la prioridad y asígnalo: al guardar cualquier cambio deja de marcarse para revisión.
          </p>
        )}
        {flash && <p role="status" className="rounded-md bg-accent-soft px-4 py-2 text-sm text-accent-dark">{flash}</p>}
        {error && !pending && !reclassify && <p role="alert" className="rounded-md bg-[#FBEAEA] px-4 py-2 text-sm text-[#8F1D1D]">{error}</p>}

        <div className="rounded-lg border border-line bg-white px-4 pb-3 pt-4">
          <StageLine ticket={ticket} audit={query.data.audit} />
        </div>

        <div className="flex flex-wrap items-start gap-5">
          <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-5">
            <Card title="Descripción">
              <p className="whitespace-pre-line text-sm leading-relaxed">{ticket.description}</p>
              <p className="mt-3 text-xs text-muted">Solicitado por {ticket.requester.name}{ticket.requester.email ? ` · ${ticket.requester.email}` : ''}</p>
            </Card>

            {ticket.resolution && (
              <Card title="Resolución" aside={<span className="text-xs text-muted">{dateTime(ticket.resolution.at)}</span>}>
                <p className="text-sm font-medium">{RESOLUTION_LABEL[ticket.resolution.code]}</p>
                <p className="mt-1 whitespace-pre-line text-sm">{ticket.resolution.notes}</p>
                <p className="mt-2 text-xs text-muted">Por {ticket.resolution.by.name}</p>
              </Card>
            )}
            {ticket.closeReason && (
              <Card title="Motivo de cierre"><p className="text-sm">{ticket.closeReason}</p></Card>
            )}

            <Conversation number={ticket.number} open={ticket.open} />

            <Card title="Historial" collapsible defaultOpen={false} aside={<span className="text-xs text-muted">{query.data.audit.length} movimientos</span>}>
              <ol className="mt-1">
                {query.data.audit.map((entry, i) => <AuditItem key={i} entry={entry} />)}
              </ol>
            </Card>
          </div>

          <aside className="flex max-w-full flex-[1_1_320px] flex-col gap-5">
            <TimePanel number={ticket.number} initiativeId={ticket.initiativeId ?? null} initiativeCode={ticket.initiativeCode ?? null} open={ticket.open} status={ticket.status} release={ticket.hoursRelease ?? null}
              canRelease={me?.deskRole === 'admin' || me?.deskRole === 'team_lead'} />
            {ticket.initiativeId && <ContractPanel number={ticket.number} />}

            <Card title="Propiedades" collapsible>
              <div className="flex flex-col gap-3 text-sm">
                <div>
                  <label htmlFor="p-group" className={label}>Grupo de resolución</label>
                  <select id="p-group" className={`mt-1 ${small}`} disabled={!editable || update.isPending}
                    value={ticket.groupId ?? ''} onChange={e => update.mutate({ groupId: e.target.value || null })}>
                    <option value="">Sin grupo</option>
                    {meta.data?.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    {ticket.groupId && !meta.data?.groups.some(g => g.id === ticket.groupId) && (
                      <option value={ticket.groupId}>{ticket.groupName ?? ticket.groupId} (inactivo)</option>
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="p-assignee" className={label}>Agente</label>
                  <div className="mt-1 flex gap-2">
                    <select id="p-assignee" className={small} disabled={!editable || update.isPending}
                      value={ticket.assigneeId ?? ''} onChange={e => update.mutate({ assigneeId: e.target.value || null })}>
                      <option value="">Sin asignar</option>
                      <AgentOptions meta={meta.data} groupId={ticket.groupId} />
                    </select>
                    {editable && me && ticket.assigneeId !== me.uid && (
                      <button type="button" className="h-9 whitespace-nowrap rounded-md border border-[#D6D3CD] px-2 text-xs hover:bg-paper"
                        onClick={() => update.mutate({ assigneeId: me.uid })}>Asignarme</button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="p-impact" className={label}>Impacto</label>
                    <select id="p-impact" className={`${small} mt-1`} disabled={!editable} value={ticket.impact}
                      onChange={e => update.mutate({ impact: e.target.value })}>
                      {LEVELS.map(l => <option key={l} value={l}>{IMPACT_LABEL[l].split('·')[0]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="p-urgency" className={label}>Urgencia</label>
                    <select id="p-urgency" className={`${small} mt-1`} disabled={!editable} value={ticket.urgency}
                      onChange={e => update.mutate({ urgency: e.target.value })}>
                      {LEVELS.map(l => <option key={l} value={l}>{URGENCY_LABEL[l]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <span className={label}>Prioridad</span>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2"><PriorityBadge priority={ticket.priority} />
                      <span className="text-xs text-muted">{ticket.priorityOverride ? 'manual' : 'calculada'}</span></span>
                    {editable && (
                      <button type="button" className="text-xs text-accent underline" onClick={() => setOverrideOpen(true)}>
                        {ticket.priorityOverride ? 'Cambiar' : 'Ajustar manualmente'}
                      </button>
                    )}
                  </div>
                  {ticket.priorityOverride && <p className="mt-1 text-xs italic text-muted">“{ticket.priorityOverride.reason}”</p>}
                </div>
                <div>
                  <label htmlFor="p-cat" className={label}>Categoría</label>
                  <select id="p-cat" className={`${small} mt-1`} disabled={!editable} value={ticket.category?.id ?? ''}
                    onChange={e => update.mutate({ categoryId: e.target.value || null })}>
                    <option value="">Sin categoría</option>
                    {meta.data?.categories.map(c => <option key={c.id} value={c.id}>{c.service} › {c.module}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="p-ini" className={label}>Iniciativa (Veta)</label>
                  <select id="p-ini" className={`${small} mt-1`} disabled={!editable} value={ticket.initiativeId ?? ''}
                    onChange={e => update.mutate({ initiativeId: e.target.value || null })}>
                    <option value="">Sin iniciativa</option>
                    {ticket.initiativeId && !initiatives.data?.some(i => i.id === ticket.initiativeId) && (
                      <option value={ticket.initiativeId}>{ticket.initiativeCode} · {ticket.initiativeName}</option>
                    )}
                    {initiatives.data?.map(i => <option key={i.id} value={i.id}>{i.code} · {i.name}</option>)}
                  </select>
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-t border-[#EFEDE9] pt-3 text-xs">
                  <dt className="text-muted">Cliente</dt><dd>{ticket.organizationName}</dd>
                  <dt className="text-muted">Solicitante</dt><dd>{ticket.requester.name}</dd>
                  <dt className="text-muted">Canal</dt><dd>{CHANNEL_LABEL[ticket.channel]}</dd>
                  <dt className="text-muted">Actualizado</dt><dd>{dateTime(ticket.updatedAt)}</dd>
                  {ticket.reopenCount > 0 && (<><dt className="text-muted">Reaperturas</dt><dd>{ticket.reopenCount}</dd></>)}
                </dl>
              </div>
            </Card>

            {needsEstimate(ticket.type) && (
              <Card title={me?.modules?.change ? 'Cambio (Veta Change)' : 'Estimado'} collapsible
                defaultOpen={['new', 'assigned', 'estimating', 'pending_approval', 'approved'].includes(ticket.status)} aside={meta.data && <span className="text-xs text-muted">bolsa ≤ {meta.data.sizing.bagMaxHours} h · proyecto &gt; {meta.data.sizing.reqMaxHours} h</span>}>
                {me?.modules?.change && ticket.type === 'REQ'
                  ? <ChangeCard number={ticket.number} fallback={<EstimateQuote ticket={ticket} sizing={meta.data?.sizing} isAdmin={me?.deskRole === 'admin'} quoteExpired={Boolean(query.data.quoteExpired)}
                  onChanged={msg => { setError(''); setFlash(msg); refresh(); }} onError={onError} />} />
                  : <EstimateQuote ticket={ticket} sizing={meta.data?.sizing} isAdmin={me?.deskRole === 'admin'} quoteExpired={Boolean(query.data.quoteExpired)}
                  onChanged={msg => { setError(''); setFlash(msg); refresh(); }} onError={onError} />}
              </Card>
            )}
          </aside>
        </div>
      </div>

      {pending && (
        <TransitionDialog ticket={ticket} option={pending} onClose={() => setPending(null)}
          onDone={(msg) => { setPending(null); setFlash(msg); refresh(); }}
          onReclassify={(type) => { setPending(null); setReclassify(type); }} />
      )}
      {reclassify && (
        <ReclassifyDialog ticket={ticket} initial={reclassify} onClose={() => setReclassify(null)}
          onDone={() => { setReclassify(null); setFlash('Ticket reclasificado; el número no cambia.'); refresh(); }} />
      )}
      {overrideOpen && (
        <OverrideDialog ticket={ticket} onClose={() => setOverrideOpen(false)}
          onSave={(body) => { update.mutate(body); setOverrideOpen(false); }} />
      )}
    </Shell>
  );
}

function TransitionDialog({ ticket, option, onClose, onDone, onReclassify }: {
  ticket: Ticket; option: TransitionOption; onClose: () => void; onDone: (msg: string) => void;
  onReclassify: (type: TicketType) => void;
}) {
  const [form, setForm] = useState<Record<string, string>>({
    holdReason: 'customer', resolutionCode: 'permanent_fix',
    estimateHours: option.to === 'converted' && ticket.estimate ? String(ticket.estimate.hours) : ''
  });
  const [error, setError] = useState<ApiError | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setForm(f => ({ ...f, [k]: e.target.value }));
  const needs = (r: string) => option.requires.includes(r as never);

  const mutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { to: option.to };
      if (needs('holdReason')) Object.assign(body, { holdReason: form.holdReason, ...(form.holdReason === 'third_party' ? { holdNote: form.holdNote } : {}) });
      if (needs('resolution')) Object.assign(body, { resolutionCode: form.resolutionCode, resolutionNotes: form.resolutionNotes });
      if (needs('reason') || option.reopen) body.reason = form.reason;
      if (needs('estimate')) Object.assign(body, { estimateHours: Number(form.estimateHours), estimateNotes: form.estimateNotes });
      if (needs('approval')) Object.assign(body, { approvedBy: form.approvedBy, approvalEvidence: form.approvalEvidence });
      return api(`/tickets/${ticket.number}/transitions`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => onDone(`Estado: ${STATUS_LABEL[option.to]}.`),
    onError: (err) => setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', 'Algo salió mal. Inténtalo de nuevo.'))
  });

  const submit = (e: FormEvent) => { e.preventDefault(); setError(null); mutation.mutate(); };
  const lbl = 'mb-1.5 block text-sm font-medium';
  const suggested = error?.body?.suggestedType as TicketType | undefined;

  return (
    <Modal title={actionLabel(ticket, option)} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {needs('holdReason') && (
          <div>
            <label htmlFor="t-hold" className={lbl}>Motivo de la espera</label>
            <select id="t-hold" className={field} value={form.holdReason} onChange={set('holdReason')}>
              {HOLD_REASONS.map(h => <option key={h} value={h}>{HOLD_REASON_LABEL[h]}</option>)}
            </select>
            {form.holdReason === 'third_party' && (
              <div className="mt-3">
                <label htmlFor="t-holdnote" className={lbl}>Con quién y referencia</label>
                <input id="t-holdnote" className={field} required minLength={3} placeholder="Ej. SAP · incidente 123456/2026" value={form.holdNote ?? ''} onChange={set('holdNote')} />
                <p className="mt-1 text-xs text-muted">El SLA se pausa mientras se espera al tercero.</p>
              </div>
            )}
          </div>
        )}
        {needs('resolution') && (
          <>
            <div>
              <label htmlFor="t-code" className={lbl}>Código de resolución</label>
              <select id="t-code" className={field} value={form.resolutionCode} onChange={set('resolutionCode')}>
                {RESOLUTION_CODES.map(c => <option key={c} value={c}>{RESOLUTION_LABEL[c]}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="t-notes" className={lbl}>Qué se hizo</label>
              <textarea id="t-notes" rows={4} className={area} required minLength={10} value={form.resolutionNotes ?? ''} onChange={set('resolutionNotes')} />
            </div>
          </>
        )}
        {needs('estimate') && (
          <>
            <div>
              <label htmlFor="t-hours" className={lbl}>Horas estimadas</label>
              <input id="t-hours" type="number" min="0.5" step="0.5" required className={field} value={form.estimateHours} onChange={set('estimateHours')} />
            </div>
            <div>
              <label htmlFor="t-enotes" className={lbl}>Alcance y supuestos</label>
              <textarea id="t-enotes" rows={3} className={area} value={form.estimateNotes ?? ''} onChange={set('estimateNotes')} />
            </div>
          </>
        )}
        {needs('approval') && (
          <>
            <div>
              <label htmlFor="t-by" className={lbl}>¿Quién del cliente aprobó?</label>
              <input id="t-by" required minLength={3} className={field} value={form.approvedBy ?? ''} onChange={set('approvedBy')} placeholder="Nombre y puesto" />
            </div>
            <div>
              <label htmlFor="t-ev" className={lbl}>Evidencia</label>
              <input id="t-ev" className={field} value={form.approvalEvidence ?? ''} onChange={set('approvalEvidence')} placeholder="p. ej. correo del 09-oct, asunto …" />
              <p className="mt-1 text-xs text-muted">A partir de la Etapa 1b el cliente aprueba desde su portal.</p>
            </div>
          </>
        )}
        {(needs('reason') || option.reopen) && (
          <div>
            <label htmlFor="t-reason" className={lbl}>{option.reopen ? 'Por qué se reabre (opcional)' : 'Motivo'}</label>
            <textarea id="t-reason" rows={3} className={area} required={needs('reason')} minLength={needs('reason') ? 5 : undefined}
              value={form.reason ?? ''} onChange={set('reason')} />
          </div>
        )}
        {option.requires.length === 0 && !option.reopen && (
          <p className="text-sm text-muted">El ticket pasará a «{STATUS_LABEL[option.to]}».</p>
        )}

        {error && (
          <div role="alert" className="rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">
            {error.message}
            {suggested && suggested !== 'PRY' && (
              <button type="button" className="ml-2 font-semibold underline" onClick={() => onReclassify(suggested)}>Reclasificar a {suggested}</button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className={btnPrimary} disabled={mutation.isPending}>{mutation.isPending ? 'Guardando…' : 'Confirmar'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ReclassifyDialog({ ticket, initial, onClose, onDone }: {
  ticket: Ticket; initial: TicketType; onClose: () => void; onDone: () => void;
}) {
  const options = (['INC', 'SOL', 'REQ'] as TicketType[]).filter(ty => ty !== ticket.type);
  const [type, setType] = useState<TicketType>(options.includes(initial) ? initial : options[0]!);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () => api(`/tickets/${ticket.number}/reclassify`, { method: 'POST', body: JSON.stringify({ type, reason }) }),
    onSuccess: onDone,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Algo salió mal. Inténtalo de nuevo.')
  });
  return (
    <Modal title="Reclasificar ticket" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="flex flex-col gap-4">
        <p className="text-sm text-muted">El número {ticket.number} no cambia. El cambio queda en el historial.</p>
        <div>
          <label htmlFor="r-type" className="mb-1.5 block text-sm font-medium">Nuevo tipo</label>
          <select id="r-type" className={field} value={type} onChange={e => setType(e.target.value as TicketType)}>
            {options.map(ty => <option key={ty} value={ty}>{TICKET_TYPE_LABEL[ty]} ({ty})</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="r-reason" className="mb-1.5 block text-sm font-medium">Motivo</label>
          <textarea id="r-reason" rows={3} required minLength={5} className={area} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-sm text-[#8F1D1D]">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className={btnPrimary} disabled={mutation.isPending}>Reclasificar</button>
        </div>
      </form>
    </Modal>
  );
}

function OverrideDialog({ ticket, onClose, onSave }: {
  ticket: Ticket; onClose: () => void; onSave: (body: Record<string, unknown>) => void;
}) {
  const [value, setValue] = useState<Priority>(ticket.priorityOverride?.value ?? ticket.priority);
  const [reason, setReason] = useState('');
  return (
    <Modal title="Ajustar prioridad" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave({ priorityOverride: { value, reason } }); }} className="flex flex-col gap-4">
        <p className="text-sm text-muted">La prioridad se calcula con impacto × urgencia. Un ajuste manual requiere justificación y queda en el historial.</p>
        <div>
          <label htmlFor="o-val" className="mb-1.5 block text-sm font-medium">Prioridad</label>
          <select id="o-val" className={field} value={value} onChange={e => setValue(e.target.value as Priority)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="o-reason" className="mb-1.5 block text-sm font-medium">Justificación</label>
          <textarea id="o-reason" rows={3} required minLength={5} className={area} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {ticket.priorityOverride && (
            <button type="button" className={btnSecondary} onClick={() => onSave({ priorityOverride: null })}>Volver a la calculada</button>
          )}
          <button type="button" className={btnSecondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className={btnPrimary}>Guardar</button>
        </div>
      </form>
    </Modal>
  );
}
